/*
 * Event Settings — same form as Create, pre-filled, PATCHes on submit.
 *
 * Save button sits in the hero row at the top of the page so it's always
 * in reach. The bottom form button was removed. After a successful save
 * a green "Saved" banner fades in and auto-dismisses after 5 seconds.
 */

import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Check } from 'lucide-react';

import { api } from '@/lib/api';
import { useTournamentStore } from '@/store/tournamentStore';
import { AppShell } from '@/components/AppShell';
import { ImageCropUpload } from '@/components/ImageCropUpload';
import { InfoHint } from '@/components/InfoHint';
import { WINNING_RULES, VISIBILITY_OPTIONS, MethodHintBody, BaselineHintBody } from './_shared';
import type { WinningRuleType, EventVisibility } from '@golf-app/shared';

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const POINTS_PRESETS = [0.5, 1, 2, 3];
const SETTINGS_FORM_ID = 'event-settings-form';

export default function EditTournament() {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const { activeEvent, setActiveEvent } = useTournamentStore();

  const initial = activeEvent?.id === eventId ? activeEvent : null;

  // Owner or captain per the roles spec. Backend gates the actual PATCH;
  // frontend bounces players away rather than letting them fill out a
  // form they can't submit. Detection is best-effort — the store may
  // still be loading on a deep link, in which case we render normally
  // and re-evaluate after fetch.
  useEffect(() => {
    if (initial && initial.myRole && initial.myRole !== 'owner' && initial.myRole !== 'captain') {
      navigate(`/app/tournaments/${eventId}`, { replace: true });
    }
  }, [initial, eventId, navigate]);

  const [loading, setLoading] = useState(!initial);
  const [name, setName] = useState(initial?.name ?? '');
  const [location, setLocation] = useState(initial?.locationName ?? '');
  const [startDate, setStartDate] = useState(initial?.startDate?.slice(0, 10) ?? '');
  const [endDate, setEndDate] = useState(initial?.endDate?.slice(0, 10) ?? '');
  const [pointsPerMatch, setPointsPerMatch] = useState(String(initial?.defaultPointsPerMatch ?? 1));
  const [winningRule, setWinningRule] = useState<WinningRuleType>(initial?.winningRuleType ?? 'most_points_wins');
  const [visibility, setVisibility] = useState<EventVisibility>(initial?.visibility ?? 'private');
  const [pointsOverride, setPointsOverride] = useState(initial?.pointsNeededOverride != null ? String(initial.pointsNeededOverride) : '');
  const [defendingTeamId, setDefendingTeamId] = useState(initial?.defendingTeamId ?? '');
  const [logoUrl, setLogoUrl] = useState(initial?.logoUrl ?? '');
  // Event-level handicap defaults. Per-round overrides live on the round
  // detail screen; this is the fallback every round inherits.
  const [handicapMethod, setHandicapMethod] = useState<'course' | 'straight'>(
    initial?.defaultHandicapMethod ?? 'course',
  );
  const [allowancePct, setAllowancePct] = useState(
    String(initial?.defaultAllowancePct ?? 100),
  );
  const [baselineMethod, setBaselineMethod] = useState<'low_man_zero' | 'full_difference'>(
    initial?.defaultBaselineMethod ?? 'low_man_zero',
  );
  // Legacy fallback for responses from an API that does not include the
  // field. New events default to showing the creator in the roster.
  const [ownerRunsInBackground, setOwnerRunsInBackground] = useState(
    initial?.ownerRunsInBackground ?? true,
  );
  const isOwner = initial?.myRole === 'owner';

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Auto-dismiss the success banner after 5 seconds. Re-saving resets
  // the timer because `success` flips back to non-null, which restarts
  // the effect.
  useEffect(() => {
    if (!success) return;
    const t = setTimeout(() => setSuccess(null), 5000);
    return () => clearTimeout(t);
  }, [success]);

  const fetchEvent = useCallback(async () => {
    if (!eventId) return;
    try {
      const ev = await api.getTournament(eventId);
      setActiveEvent(ev);
      setName(ev.name); setLocation(ev.locationName ?? '');
      setStartDate(ev.startDate?.slice(0, 10) ?? '');
      setEndDate(ev.endDate?.slice(0, 10) ?? '');
      setPointsPerMatch(String(ev.defaultPointsPerMatch));
      setWinningRule(ev.winningRuleType); setVisibility(ev.visibility);
      setPointsOverride(ev.pointsNeededOverride != null ? String(ev.pointsNeededOverride) : '');
      setDefendingTeamId(ev.defendingTeamId ?? '');
      setLogoUrl(ev.logoUrl ?? '');
      setHandicapMethod(ev.defaultHandicapMethod ?? 'course');
      setAllowancePct(String(ev.defaultAllowancePct ?? 100));
      setBaselineMethod(ev.defaultBaselineMethod ?? 'low_man_zero');
      setOwnerRunsInBackground(ev.ownerRunsInBackground ?? true);
      // Same role gate as the initial-from-store path. After fetch we now
      // know for sure whether this user is allowed.
      if (ev.myRole && ev.myRole !== 'owner' && ev.myRole !== 'captain') {
        navigate(`/app/tournaments/${eventId}`, { replace: true });
        return;
      }
    } finally {
      setLoading(false);
    }
  }, [eventId, setActiveEvent, navigate]);

  useEffect(() => { if (!initial) fetchEvent(); }, [initial, fetchEvent]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    if (!eventId) return;
    if (name.trim().length < 2) { setError('Event name must be at least 2 characters.'); return; }
    if (startDate && !DATE_RE.test(startDate)) { setError('Start date must be YYYY-MM-DD.'); return; }
    if (endDate && !DATE_RE.test(endDate)) { setError('End date must be YYYY-MM-DD.'); return; }
    // Order check. Both dates are YYYY-MM-DD strings, so lexicographic
    // comparison matches calendar order. Without this an owner could
    // submit endDate < startDate and the backend would store nonsense.
    if (startDate && endDate && endDate < startDate) {
      setError('End date cannot be before start date.');
      return;
    }
    const ppm = parseFloat(pointsPerMatch);
    if (!Number.isFinite(ppm) || ppm <= 0) { setError('Points per match must be positive.'); return; }

    const pctNum = parseFloat(allowancePct);
    if (!Number.isFinite(pctNum) || pctNum < 0 || pctNum > 200) {
      setError('Allowance % must be a number between 0 and 200.');
      return;
    }

    const body: Record<string, unknown> = {
      name: name.trim(),
      locationName: location.trim() || null,
      defaultPointsPerMatch: ppm,
      winningRuleType: winningRule,
      visibility,
      defendingTeamId: defendingTeamId || null,
      // logoUrl, startDate and endDate are all tri-state: send the value
      // when set, send explicit null when cleared. The backend treats
      // `undefined` as "keep" and null as "clear". Earlier this code only
      // included startDate/endDate when truthy, which omitted them
      // entirely on clear and the backend kept the old value.
      logoUrl: logoUrl || null,
      startDate: startDate || null,
      endDate: endDate || null,
      defaultHandicapMethod: handicapMethod,
      defaultAllowancePct: pctNum,
      defaultBaselineMethod: baselineMethod,
    };
    // Owner-only. Captains can edit settings but can't change whether the
    // owner appears in the roster, so only send it when the owner is editing.
    if (isOwner) {
      body.ownerRunsInBackground = ownerRunsInBackground;
    }
    if (pointsOverride.trim()) {
      const po = parseFloat(pointsOverride);
      if (!Number.isFinite(po) || po < 0) { setError('Points override must be a non-negative number.'); return; }
      body.pointsNeededOverride = po;
    } else {
      body.pointsNeededOverride = null;
    }

    setSubmitting(true);
    try {
      await api.updateTournament(eventId, body);
      // Refresh the cached event so other screens (Teams, Rounds, etc)
      // pick up the new fields without a hard reload.
      const ev = await api.getTournament(eventId);
      setActiveEvent(ev);
      setSuccess('Changes saved.');
    } catch (e: any) {
      setError(e?.message ?? 'Failed to save');
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) {
    return (
      <AppShell back={{ to: `/app/tournaments/${eventId ?? ''}`, label: 'Event' }}>
        <div className="t-loading"><div className="spinner" /></div>
      </AppShell>
    );
  }

  return (
    <AppShell back={{ to: `/app/tournaments/${eventId}`, label: 'Event' }}>
      <section className="t-hero">
        <div className="t-hero-row">
          <div>
            <h1>Event Settings</h1>
            <p>Update the basics, scoring rules, or pick a defending champion.</p>
          </div>
          {/* form="…" lets this button submit the form below even though
              it lives outside it in the DOM. Modern-browser standard. */}
          <button
            type="submit"
            form={SETTINGS_FORM_ID}
            className="t-btn t-btn-primary"
            disabled={submitting}
            style={{ flexShrink: 0 }}
          >
            {submitting ? 'Saving…' : 'Save Changes'}
          </button>
        </div>
      </section>


      {error && <div className="t-error">{error}</div>}

      {success && (
        <div
          role="status"
          aria-live="polite"
          style={{
            display: 'flex', alignItems: 'center', gap: 10,
            background: 'rgba(34,197,94,0.10)',
            border: '1px solid var(--green-bright)',
            color: 'var(--green-bright)',
            padding: '10px 14px',
            borderRadius: 10,
            marginBottom: 14,
            fontWeight: 700,
            // The banner can render rapidly after consecutive saves; a
            // tiny fade keeps the transition perceivable without being
            // distracting.
            animation: 'fadeIn 120ms ease-out',
          }}
        >
          <Check width={16} height={16} strokeWidth={3} />
          <span>{success}</span>
        </div>
      )}

      <form id={SETTINGS_FORM_ID} className="t-form" onSubmit={handleSubmit}>
        <div className="t-field">
          <label htmlFor="e-name" className="t-label">Event name</label>
          <input id="e-name" className="t-input" value={name} onChange={(e) => setName(e.target.value)} maxLength={80} />
        </div>
        <div className="t-field">
          <div className="t-label">Event logo</div>
          <ImageCropUpload
            value={logoUrl}
            onChange={setLogoUrl}
            emptyLabel="Upload event logo"
            alt="Event logo"
          />
        </div>
        <div className="t-field">
          <label htmlFor="e-loc" className="t-label">Location</label>
          <input id="e-loc" className="t-input" value={location} onChange={(e) => setLocation(e.target.value)} maxLength={120} />
        </div>
        <div className="t-field-row">
          <div className="t-field">
            <label htmlFor="e-start" className="t-label">Start date</label>
            <input
              id="e-start"
              type="date"
              className="t-input"
              value={startDate ?? ''}
              onChange={(e) => setStartDate(e.target.value)}
              onClick={(e) => { try { (e.currentTarget as HTMLInputElement).showPicker(); } catch {} }}
            />
          </div>
          <div className="t-field">
            <label htmlFor="e-end" className="t-label">End date</label>
            <input
              id="e-end"
              type="date"
              className="t-input"
              value={endDate ?? ''}
              onChange={(e) => setEndDate(e.target.value)}
              min={startDate || undefined}
              onClick={(e) => { try { (e.currentTarget as HTMLInputElement).showPicker(); } catch {} }}
            />
          </div>
        </div>

        <div className="t-field">
          <div className="t-label">Points per match</div>
          <div className="t-chip-row">
            {POINTS_PRESETS.map((p) => (
              <button key={p} type="button" className={`t-chip ${parseFloat(pointsPerMatch) === p ? 't-chip-active' : ''}`} onClick={() => setPointsPerMatch(String(p))}>{p}</button>
            ))}
          </div>
        </div>

        <div className="t-field">
          <label htmlFor="e-override" className="t-label">Points to win (override, optional)</label>
          <input id="e-override" className="t-input" value={pointsOverride} onChange={(e) => setPointsOverride(e.target.value)} inputMode="decimal" placeholder="Leave empty to auto-derive" />
        </div>

        <div className="t-field">
          <div className="t-label">Winning rule</div>
          {WINNING_RULES.map((r) => (
            <button key={r.value} type="button" className={`t-option ${winningRule === r.value ? 't-option-selected' : ''}`} onClick={() => setWinningRule(r.value)}>
              <span className="t-option-radio">{winningRule === r.value && <span className="t-option-radio-dot" />}</span>
              <span>
                <p className={`t-option-label ${winningRule === r.value ? 't-option-label-selected' : ''}`}>{r.label}</p>
                <p className="t-option-hint">{r.hint}</p>
              </span>
            </button>
          ))}
        </div>

        <div className="t-field">
          <div className="t-label" style={{ display: 'flex', alignItems: 'center' }}>
            Handicap method
            <InfoHint title="Handicap method" body={<MethodHintBody />} />
          </div>
          {([
            { value: 'course' as const,
              label: 'Net Course Rating',
              hint: "USGA formula. Index is adjusted by the tee's slope and rating so a player gets more pops from harder tees." },
            { value: 'straight' as const,
              label: 'Net Straight Handicap',
              hint: "Index plays straight up. 14.4 gets 14 strokes on the holes with stroke index 1-14. No tee adjustment." },
          ]).map((opt) => (
            <button
              key={opt.value}
              type="button"
              className={`t-option ${handicapMethod === opt.value ? 't-option-selected' : ''}`}
              onClick={() => setHandicapMethod(opt.value)}
            >
              <span className="t-option-radio">{handicapMethod === opt.value && <span className="t-option-radio-dot" />}</span>
              <span>
                <p className={`t-option-label ${handicapMethod === opt.value ? 't-option-label-selected' : ''}`}>{opt.label}</p>
                <p className="t-option-hint">{opt.hint}</p>
              </span>
            </button>
          ))}
        </div>

        <div className="t-field">
          <label htmlFor="e-allowance" className="t-label" style={{ display: 'flex', alignItems: 'center' }}>
            Allowance % (default 100)
            <InfoHint
              title="Match-play allowance"
              body="A percentage applied to the computed handicap before netting. Lower allowance = closer match. USGA recommends 100% (full), 90% (Singles), 85% (Four-Ball)."
              example={
                <>
                  A computed handicap of 14 at <strong>100%</strong> plays as 14. At <strong>90%</strong>{' '}
                  it plays as 13 (14 × 0.90 = 12.6 → 13). At <strong>85%</strong> it plays as 12.
                </>
              }
            />
          </label>
          <input
            id="e-allowance"
            className="t-input"
            value={allowancePct}
            onChange={(e) => setAllowancePct(e.target.value)}
            inputMode="decimal"
            placeholder="100"
          />
        </div>

        <div className="t-field">
          <div className="t-label" style={{ display: 'flex', alignItems: 'center' }}>
            Baseline (netting)
            <InfoHint title="Baseline (netting)" body={<BaselineHintBody />} />
          </div>
          {([
            { value: 'low_man_zero' as const,
              label: 'Low man zero',
              hint: "Lowest handicap in the match plays as scratch; everyone else gets the rounded difference. Standard match play." },
            { value: 'full_difference' as const,
              label: 'Full difference',
              hint: "Each player gets strokes equal to their own rounded playing handicap. No subtraction." },
          ]).map((opt) => (
            <button
              key={opt.value}
              type="button"
              className={`t-option ${baselineMethod === opt.value ? 't-option-selected' : ''}`}
              onClick={() => setBaselineMethod(opt.value)}
            >
              <span className="t-option-radio">{baselineMethod === opt.value && <span className="t-option-radio-dot" />}</span>
              <span>
                <p className={`t-option-label ${baselineMethod === opt.value ? 't-option-label-selected' : ''}`}>{opt.label}</p>
                <p className="t-option-hint">{opt.hint}</p>
              </span>
            </button>
          ))}
        </div>

        <div className="t-field">
          <div className="t-label">Visibility</div>
          {VISIBILITY_OPTIONS.map((v) => (
            <button key={v.value} type="button" className={`t-option ${visibility === v.value ? 't-option-selected' : ''}`} onClick={() => setVisibility(v.value)}>
              <span className="t-option-radio">{visibility === v.value && <span className="t-option-radio-dot" />}</span>
              <span>
                <p className={`t-option-label ${visibility === v.value ? 't-option-label-selected' : ''}`}>{v.label}</p>
                <p className="t-option-hint">{v.hint}</p>
              </span>
            </button>
          ))}
        </div>

        {isOwner && (
          <div className="t-field">
            <div className="t-label">Your spot in this event</div>
            {[
              {
                value: true,
                label: 'Run in the background',
                hint: 'You organize the event with full control but are not listed as a player.',
              },
              {
                value: false,
                label: 'Show me in the roster',
                hint: 'You appear in the player list alongside everyone else.',
              },
            ].map((opt) => (
              <button
                key={String(opt.value)}
                type="button"
                className={`t-option ${ownerRunsInBackground === opt.value ? 't-option-selected' : ''}`}
                onClick={() => setOwnerRunsInBackground(opt.value)}
              >
                <span className="t-option-radio">{ownerRunsInBackground === opt.value && <span className="t-option-radio-dot" />}</span>
                <span>
                  <p className={`t-option-label ${ownerRunsInBackground === opt.value ? 't-option-label-selected' : ''}`}>{opt.label}</p>
                  <p className="t-option-hint">{opt.hint}</p>
                </span>
              </button>
            ))}
          </div>
        )}

        {activeEvent && activeEvent.teams.length > 0 && (
          <div className="t-field">
            <div className="t-label">Defending champion</div>
            <div className="t-chip-row">
              <button type="button" className={`t-chip ${!defendingTeamId ? 't-chip-active' : ''}`} onClick={() => setDefendingTeamId('')}>
                None
              </button>
              {activeEvent.teams.map((t) => (
                <button key={t.id} type="button" className={`t-chip ${defendingTeamId === t.id ? 't-chip-active' : ''}`} onClick={() => setDefendingTeamId(t.id)}>
                  {t.name}
                </button>
              ))}
            </div>
          </div>
        )}
      </form>
    </AppShell>
  );
}
